/**
 * Services drawer — CSS-transition-based panel triggered by mobile bottom nav.
 * Does not use native <dialog>/showModal() so the bottom nav z-index sits above it.
 */

class ServicesDrawerController {
  constructor() {
    this.drawer = document.querySelector('[data-services-drawer]');
    this.overlay = document.querySelector('[data-services-drawer-overlay]');

    if (!this.drawer) return;

    this._focusableSelectors = 'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';
    this._onKeyDown = this._onKeyDown.bind(this);
    this._previousFocus = null;

    this.overlay?.addEventListener('click', () => this.close());
    this.drawer.querySelector('[data-services-drawer-close]')?.addEventListener('click', () => this.close());
  }

  get isOpen() {
    return this.drawer?.classList.contains('is-active') ?? false;
  }

  open() {
    if (!this.drawer || this.isOpen) return;

    this.drawer.classList.add('is-active');
    this.overlay?.classList.add('is-active');
    this.drawer.removeAttribute('inert');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.setAttribute('scroll-lock', '');

    this._previousFocus = document.activeElement;
    this.drawer.querySelector('[data-services-drawer-close]')?.focus();

    document.addEventListener('keydown', this._onKeyDown);
    document.dispatchEvent(new CustomEvent('services-drawer:opened'));
  }

  close() {
    if (!this.drawer || !this.isOpen) return;

    this.drawer.classList.remove('is-active');
    this.overlay?.classList.remove('is-active');
    this.drawer.setAttribute('inert', '');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.removeAttribute('scroll-lock');

    document.removeEventListener('keydown', this._onKeyDown);
    this._previousFocus?.focus();
    document.dispatchEvent(new CustomEvent('services-drawer:closed'));
  }

  _onKeyDown(e) {
    if (e.key === 'Escape') {
      this.close();
      return;
    }

    if (e.key !== 'Tab') return;

    const focusable = Array.from(
      this.drawer.querySelectorAll(this._focusableSelectors)
    ).filter((el) => !el.closest('[inert]') && el.offsetParent !== null);

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.servicesDrawer = new ServicesDrawerController();
  });
} else {
  window.servicesDrawer = new ServicesDrawerController();
}
